import React, { useState } from 'react';

import { get, map, find } from 'lodash';
import Tabs, { useTabFilter } from 'src/presentation/components/pages/shared/Tabs';
import { Table, TR, TBody, TD, TH } from '@ad-scripts/presentation/components/common/table';
import styled from 'styled-components';
import moment from 'moment';

const LogDetailsTable = ({ item }): JSX.Element | null => {
  const [currentTabConfig, setCurrentTabConfig] = useState(TAB_CONFIG[0]);

  const switchTab = () => {
    if (currentTabConfig.label === 'Response') {
      setCurrentTabConfig(TAB_CONFIG[0]);
    } else if (currentTabConfig.label === 'Request') {
      setCurrentTabConfig(TAB_CONFIG[1]);
    }
  };

  const [tabsProps] = useTabFilter({
    tabConfig: TAB_CONFIG,
    items: item,
    onTabChange: switchTab,
    preselectedTab: null,
    updateUrlOnTabChange: false,
    root: '',
  });
  const currentTabTitle = tabsProps.selectedTabLabel;
  const currentTab = find(TAB_CONFIG, ['label', currentTabTitle]);
  const renderActiveTab = get(currentTab, 'renderRow');

  return (
    <TableContainer key={item.id} data-aid={`log-details-table`}>
      <TabSpan>
        <Tabs dataAid={'log-tabs'} {...tabsProps} />
        <Duration data-aid={'duration'}>Took {moment.duration(item.duration).asSeconds() + 's'}</Duration>
      </TabSpan>
      <StyledLogTable>
        <TBody>{renderActiveTab!(item)}</TBody>
      </StyledLogTable>
    </TableContainer>
  );
};

const renderRequest = log => {
  const headerKeyRows = map(log.request.headers, (headerKey, index) => {
    return (
      <TR key={`request-header-key-${log.id}[${index}]`} data-aid={`request-header-key-${log.id}[${index}]`}>
        <TD>{headerKey}</TD>
      </TR>
    );
  });

  return (
    <TR key={`request-main-row-${log.id}`}>
      <TableCell>
        <StyledLogTable>
          <TBody>
            <TR key={`request-information-${log.id}`}>
              <StyledSubTH>Request Information</StyledSubTH>
            </TR>
            <TR>
              <TD data-aid={'request-url'}>URL: {log.request.url}</TD>
            </TR>
            <TR>
              <TD data-aid={'request-method'}>Method: {log.request.method}</TD>
            </TR>
            <TR>
              <StyledSubTH>Header Keys</StyledSubTH>
            </TR>
            {headerKeyRows}
            <TR>
              <StyledSubTH>Payload</StyledSubTH>
            </TR>
            <TR>
              <StyledTD>
                <pre data-aid={'request-payload'}>{JSON.stringify(tryParseJSON(log.request.body), null, 2)}</pre>
              </StyledTD>
            </TR>
          </TBody>
        </StyledLogTable>
      </TableCell>
    </TR>
  );
};

const renderResponse = log => {
  let tableBody;

  if (log.response) {
    const headerKeyRows = map(log.response.headers, (headerKey, index) => {
      return (
        <TR key={`response-header-key-${log.id}[${index}]`} data-aid={`response-header-key-${log.id}[${index}]`}>
          <TD>{headerKey}</TD>
        </TR>
      );
    });

    const bodyValue = log.response.body ? (
      <pre>{JSON.stringify(tryParseJSON(log.response.body), null, 2)}</pre>
    ) : (
      <div>No response was returned</div>
    );

    tableBody = (
      <>
        {log.response.status && log.response.status !== 0 ? (
          <TR>
            <TD data-aid={'response-status'}>Status: {log.response.status}</TD>
          </TR>
        ) : null}
        <TR>
          <StyledSubTH>Header Keys</StyledSubTH>
        </TR>
        {headerKeyRows}
        <TR>
          <StyledSubTH>Body</StyledSubTH>
        </TR>
        <TR>
          <StyledTD data-aid={'response-body'}>{bodyValue}</StyledTD>
        </TR>
      </>
    );
  } else {
    tableBody = (
      <>
        <TR>
          <TD data-aid={'no-response-body'}>
            <div>There was no response.</div>
          </TD>
        </TR>
      </>
    );
  }

  return (
    <TR>
      <TableCell>
        <StyledLogTable>
          <TBody>
            <TR>
              <StyledSubTH>Response Information</StyledSubTH>
            </TR>
            {log.error?.code ? (
              <TR>
                <TD data-aid={'response-code'}>Code: {log.error.code}</TD>
              </TR>
            ) : null}
            {tableBody}
          </TBody>
        </StyledLogTable>
      </TableCell>
    </TR>
  );
};

const TAB_CONFIG = [
  {
    columns: [],
    itemsKey: 'log',
    itemsName: 'request',
    key: 'request-key',
    label: 'Request',
    renderRow: renderRequest,
    showContentIfNoItems: true,
  },
  {
    columns: [],
    itemsKey: 'log',
    itemsName: 'response',
    key: 'response-key',
    label: 'Response',
    renderRow: renderResponse,
    showContentIfNoItems: true,
  },
];

const tryParseJSON = payload => {
  try {
    const obj = JSON.parse(payload);
    if (obj && typeof obj === 'object') {
      return obj;
    }
  } catch (e) {
    return payload;
  }
  return payload;
};

const Duration = styled.div`
  color: ${p => p.theme.colors.gray600};
  padding: 9px;
`;

const StyledLogTable = styled(Table).attrs({ zebra: false })`
  border: none;
  padding: 0;
`;

const StyledSubTH = styled(TH)`
  padding: 16px;
  position: sticky;
  color: ${p => p.theme.colors.black};
`;

const StyledTD = styled(TD)`
  > pre {
    white-space: pre-wrap;
  }
`;

const TableContainer = styled.div`
  border-block: solid;
  border-block-color: ${p => p.theme.colors.gray200};
`;

const TableCell = styled.td`
  padding: 0;
  background-color: ${p => p.theme.colors.white};
`;

const TabSpan = styled.span`
  display: flex;
  justify-content: space-between;
`;

export default LogDetailsTable;
